import { createTheme, alpha } from '@mui/material/styles';
import type { PaletteMode } from '@mui/material';

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN SYSTEM — Token-based MUI theme (Premium, light mode)
// Mirrors index.css custom properties. Brand color preserved.
// ═══════════════════════════════════════════════════════════════════════════

// Brand Colors
const PRIMARY      = '#1F7A63';
const PRIMARY_LT   = '#2A9D7E';
const PRIMARY_DK   = '#166354';
const PRIMARY_DKR  = '#0D3D2F';
// Single shared dark-green fill for EVERY table header across the app.
export const TABLE_HEADER_BG = '#166354';
// ─── APP-WIDE TABLE STANDARD (T27 + T34) ─────────────────────────────────
// ONE line colour and ONE corner radius for EVERY table in the app.
//
// TABLE_BORDER is used by *every* table line without exception: the outer
// container border, the header/body divider, the horizontal row lines and the
// vertical cell dividers. Previously only the vertical dividers carried this
// dark colour while the horizontal lines used lighter per-file hairlines.
//
// TABLE_RADIUS is the single corner radius for every table container. It is
// deliberately small (5px) — the old 10/12/14/16px containers looked far too
// rounded. Non-table surfaces (buttons, cards, chips, dialogs) keep their own
// radii; this token is for tables only.
//
// T9 — TRUE BLACK, RULED BY THE OWNER 2026-08-08.
// He asked for "table all borders should be in black" twice and the app shipped
// slate #334155 both times. Asked in prose whether the slate was dark enough he
// answered "im not getting it", which is the correct answer to an unanswerable
// question: a COLOUR cannot be judged in text. T9 sat open for fifteen sessions
// on the strength of that one question. The rule this leaves behind is in
// START_HERE — never ask a visual question in words; ship what was literally
// asked for, or show two screenshots.
// Reverting is the same single token, because the standard is theme-enforced
// (mistake shape 18): every table in the app reads this constant, so nothing
// needs sweeping at the call sites and nothing can opt out.
export const TABLE_BORDER = '#000000';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   T322 — THE ONE RADIUS SCALE. His words: *"make all the table design style in one common
   variable when that is changed all the table styling changes, and make the corner radius
   minimal and standard, too much and too less kills the ui styling"*.

   ⚠ HE IS DESCRIBING A REAL DEFECT, NOT A TASTE: the app had 4, 6, 7, 8, 10, 12, 14, 16, 20 and
   24px radii, retyped per file, so a settings tab was SQUARE while the card beside it was nearly
   a pill. Neither number was wrong on its own; having nine of them is what looks broken.

   FIVE steps, and every one of them is a step you can SEE — a scale with values a viewer cannot
   tell apart is the same disease. Nothing outside this object may invent a radius:
     · `xs`  chips, small pills, inline tags
     · `sm`  the table container, tabs, inputs — the workhorse
     · `md`  buttons, menus, popovers
     · `lg`  cards and panels
     · `xl`  dialogs and the few large surfaces that frame a whole screen
   ══════════════════════════════════════════════════════════════════════════════════════════════ */
export const RADIUS = { xs: 4, sm: 6, md: 8, lg: 10, xl: 12, pill: 999 } as const;

/** The table's corner, from the scale. Change `RADIUS.sm` and every table follows. */
export const TABLE_RADIUS = `${RADIUS.sm}px`;
/** Ready-made `1px solid <standard colour>` line for any table edge. */
export const TABLE_BORDER_LINE = `1px solid ${TABLE_BORDER}`;

/**
 * Shared `sx` for ANY table container — a MUI <TableContainer>, a <Paper>/<Box>
 * wrapping a <Table>, or a wrapper around a native <table>.
 *
 * Spread it first so local layout props (mb, mx, maxHeight, minWidth …) can be
 * added after it:
 *
 *   <TableContainer sx={{ ...tableSx, mb: 3 }}>
 *
 * It guarantees:
 *   • the standard 5px radius with correct corner clipping (overflow hidden),
 *     so the dark header fill follows the rounded top corners;
 *   • the standard border colour on the container itself; and
 *   • the SAME colour on every cell / header-cell edge inside it, beating any
 *     leftover per-file hairline colours.
 */
export const tableSx = {
  border: TABLE_BORDER_LINE,
  borderRadius: TABLE_RADIUS,
  overflow: 'hidden',
  // One colour for every line inside — body cells, header cells and the native
  // <th>/<td> used by the hand-rolled tables.
  '& .MuiTableCell-root, & .MuiTableCell-head, & th, & td': {
    borderColor: `${TABLE_BORDER} !important`,
  },
} as const;

/**
 * Same standard for a bare <table>/<Box component="table"> that has no wrapper
 * of its own: draws the full grid in the standard colour and rounds the table
 * itself. The outer edges are left to the container/table border so the grid
 * never doubles up.
 */
/**
 * T216 — THE SEAM MODEL, AND IT IS A ROOT-CAUSE FIX, NOT A COVER-UP.
 *
 * His words: *"rest of the pages table left header still show 95% green and like 10%
 * white ... only inventory page of that part looks ok"*.
 *
 * MEASURED, not guessed. On every `border-collapse: collapse` table the header ROW's
 * box starts **1.33 CSS px right of the TABLE's own box** — at his device pixel ratio
 * of 1.5 that is exactly 2 device pixels. Those 2 pixels are inside the container's
 * clip and belong to the table element, which is transparent, so the white page card
 * shows through them. Against a dark green header it reads as a white sliver down the
 * left edge. Inventory was the one screen he said looked right, and Inventory is the
 * one table already using `separate` — its band measures **0**.
 *
 * So the fix is not to paint over the band (a `box-shadow` on the end header cells was
 * tried first and works, but it is a smear that can bleed out of any container that
 * does not clip). The fix is to stop creating it.
 *
 * ⚠ `borderSpacing: 0` IS NOT OPTIONAL. `separate` without it reintroduces the default
 * 2px gap between every cell and the grid falls apart. And it is safe to switch here
 * only because every cell in this codebase draws `borderRight` + `borderBottom` and
 * never `borderLeft`/`borderTop` — so neighbours contribute ONE line each way with or
 * without collapsing. That is exactly why Inventory never doubled up.
 */
export const TABLE_SEAMS = {
  borderCollapse: 'separate' as const,
  borderSpacing: 0,
};

export const nativeTableSx = {
  width: '100%',
  ...TABLE_SEAMS,
  '& th, & td': {
    /* T329 — the full grid, and the hand-rolled tables draw it WITH the MUI ones. ⚠ Under
       `separate` (T216) cells must draw right+bottom and never left+top, or every internal seam
       becomes two lines. */
    borderRight: TABLE_BORDER_LINE,
    borderBottom: TABLE_BORDER_LINE,
    borderColor: `${TABLE_BORDER} !important`,
  },
  '& th:last-of-type, & td:last-of-type': { borderRight: 'none' },
  '& tbody tr:last-of-type td': { borderBottom: 'none' },
} as const;

// Light mode palette (refined: slightly cooler & more layered)
const c = {
  bgCanvas:    '#f5f7fa',
  bgSurface:   '#ffffff',
  bgSurface2:  '#fafbfc',
  bgSurface3:  '#f4f6f9',
  bgInput:     '#f6f8fb',
  border:      '#e4e8ee',
  borderSubtle:'#eef1f5',
  borderHairline:'#f2f4f7',
  borderStrong:'#d6dbe2',
  textPrimary: '#0b1220',
  textSecondary:'#4a5365',
  textMuted:   '#8b93a3',
  textDisabled:'#b6bcc7',
};

// Premium gradients
const GRAD_PRIMARY       = `linear-gradient(135deg, ${PRIMARY_LT} 0%, ${PRIMARY} 55%, ${PRIMARY_DK} 100%)`;
const GRAD_PRIMARY_HOVER = `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DK} 55%, ${PRIMARY_DKR} 100%)`;
const SURFACE_GRADIENT   = 'linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)';

// Ring shadow for focus states
const RING_PRIMARY        = `0 0 0 3px ${alpha(PRIMARY, 0.18)}`;
const RING_PRIMARY_STRONG = `0 0 0 4px ${alpha(PRIMARY, 0.22)}`;

export const createAppTheme = (_mode?: PaletteMode) => {
  // Multi-layer shadows (Linear/Vercel style: ultra-soft + crisp edge)
  const SHADOW_XS  = '0 1px 2px rgba(15, 23, 42, 0.04)';
  const SHADOW_SM  = '0 1px 2px rgba(15, 23, 42, 0.04), 0 1px 3px rgba(15, 23, 42, 0.05)';
  const SHADOW_MD  = '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px -2px rgba(15, 23, 42, 0.08)';
  const SHADOW_LG  = '0 1px 2px rgba(15, 23, 42, 0.04), 0 10px 24px -6px rgba(15, 23, 42, 0.10), 0 20px 48px -12px rgba(15, 23, 42, 0.10)';
  const SHADOW_XL  = '0 1px 2px rgba(15, 23, 42, 0.04), 0 16px 32px -8px rgba(15, 23, 42, 0.12), 0 32px 64px -16px rgba(15, 23, 42, 0.14)';
  const SHADOW_2XL = '0 2px 4px rgba(15, 23, 42, 0.05), 0 24px 48px -12px rgba(15, 23, 42, 0.18), 0 48px 96px -24px rgba(15, 23, 42, 0.20)';

  return createTheme({
    palette: {
      mode: 'light',
      primary:    { main: PRIMARY, light: PRIMARY_LT, dark: PRIMARY_DK, contrastText: '#fff' },
      secondary:  { main: c.textSecondary, light: c.textMuted, dark: c.textPrimary, contrastText: '#fff' },
      background: { default: c.bgCanvas, paper: c.bgSurface },
      success:    { main: '#16a34a', light: '#86efac', dark: '#14532d' },
      warning:    { main: '#f59e0b', light: '#fbbf24', dark: '#d97706' },
      error:      { main: '#ef4444', light: '#fca5a5', dark: '#dc2626' },
      info:       { main: '#3b82f6', light: '#93c5fd', dark: '#166354' },
      text:       { primary: c.textPrimary, secondary: c.textSecondary },
      divider:    c.border,
    },

    typography: {
      fontFamily: '"Inter", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: { fontWeight: 800, letterSpacing: '-0.035em', fontSize: '2.125rem', lineHeight: 1.12, color: c.textPrimary, fontFeatureSettings: '"ss01", "cv11"' },
      h2: { fontWeight: 700, letterSpacing: '-0.03em',  fontSize: '1.625rem', lineHeight: 1.18, color: c.textPrimary },
      h3: { fontWeight: 700, letterSpacing: '-0.022em', fontSize: '1.3125rem',lineHeight: 1.25, color: c.textPrimary },
      h4: { fontWeight: 600, letterSpacing: '-0.018em', fontSize: '1.125rem', lineHeight: 1.3,  color: c.textPrimary },
      h5: { fontWeight: 600, letterSpacing: '-0.012em', fontSize: '1rem',     lineHeight: 1.35, color: c.textPrimary },
      h6: { fontWeight: 600, letterSpacing: '-0.005em', fontSize: '0.875rem', lineHeight: 1.4,  color: c.textPrimary },
      subtitle1: { fontWeight: 500, fontSize: '0.9375rem', color: c.textSecondary, lineHeight: 1.5, letterSpacing: '-0.005em' },
      subtitle2: { fontWeight: 700, fontSize: '0.6875rem', letterSpacing: '0.085em', textTransform: 'uppercase' as const, color: c.textMuted },
      body1: { fontSize: '0.875rem', lineHeight: 1.6, color: c.textSecondary, letterSpacing: '-0.003em' },
      body2: { fontSize: '0.8125rem', lineHeight: 1.55, color: c.textMuted },
      button: { fontWeight: 600, letterSpacing: '0.005em', textTransform: 'none' as const },
      caption: { fontSize: '0.75rem', color: c.textMuted, lineHeight: 1.4, letterSpacing: '0.005em' },
      overline: { fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.085em', textTransform: 'uppercase' as const, color: c.textMuted, lineHeight: 1.4 },
    },

    /* T322 — `sx={{ borderRadius: 2 }}` anywhere in the app means 2 × THIS. It was 12, which
       turned a casual `borderRadius: 2` into 24px. From the scale, so the loose numeric radii
       scattered through the pages land on the scale too. */
    shape: { borderRadius: RADIUS.sm },

    shadows: [
      'none', SHADOW_XS, SHADOW_SM, SHADOW_MD, SHADOW_LG, SHADOW_XL, SHADOW_2XL,
      ...Array(18).fill(SHADOW_2XL),
    ] as any,

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            backgroundColor: c.bgCanvas,
            color: c.textPrimary,
            transition: 'background-color 0.22s cubic-bezier(0.4,0,0.2,1), color 0.22s cubic-bezier(0.4,0,0.2,1)',
            '&::-webkit-scrollbar':       { width: 10, height: 10 },
            '&::-webkit-scrollbar-track':  { background: 'transparent' },
            '&::-webkit-scrollbar-thumb':  {
              background: 'transparent',
              border: '3px solid transparent',
              backgroundClip: 'padding-box',
              borderRadius: 100,
            },
            '&:hover::-webkit-scrollbar-thumb': { background: 'rgba(15,23,42,0.14)', backgroundClip: 'padding-box' },
            '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(15,23,42,0.28) !important', backgroundClip: 'padding-box !important' },
          },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true, disableRipple: false },
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: RADIUS.md,
            fontWeight: 600,
            padding: '8px 18px',
            fontSize: '0.875rem',
            letterSpacing: '0.005em',
            transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.18s ease, border-color 0.2s ease, color 0.2s ease',
            '&:focus-visible': { boxShadow: RING_PRIMARY_STRONG },
            '&.Mui-disabled': { opacity: 0.55 },
          },
          contained: {
            boxShadow: SHADOW_XS,
            '&:hover': { transform: 'translateY(-1px)', boxShadow: SHADOW_MD },
            '&:active': { transform: 'translateY(0)', boxShadow: SHADOW_XS },
          },
          containedPrimary: {
            background: GRAD_PRIMARY,
            color: '#fff',
            boxShadow: `0 1px 0 0 ${alpha('#fff', 0.18)} inset, 0 1px 2px rgba(15,23,42,0.08), 0 6px 14px -4px ${alpha(PRIMARY, 0.45)}`,
            '&:hover': {
              background: GRAD_PRIMARY_HOVER,
              boxShadow: `0 1px 0 0 ${alpha('#fff', 0.22)} inset, 0 2px 4px rgba(15,23,42,0.10), 0 12px 22px -6px ${alpha(PRIMARY, 0.55)}`,
            },
            '&:active': {
              boxShadow: `0 1px 0 0 ${alpha('#fff', 0.16)} inset, 0 1px 2px rgba(15,23,42,0.08), 0 4px 10px -4px ${alpha(PRIMARY, 0.45)}`,
            },
          },
          containedSecondary: {
            backgroundColor: c.textPrimary,
            color: '#fff',
            '&:hover': { backgroundColor: '#1f2937' },
          },
          outlined: {
            borderColor: c.border,
            color: c.textSecondary,
            backgroundColor: c.bgSurface,
            '&:hover': { backgroundColor: c.bgSurface2, borderColor: c.borderStrong, color: c.textPrimary, boxShadow: SHADOW_XS },
          },
          outlinedPrimary: {
            borderColor: alpha(PRIMARY, 0.32),
            color: PRIMARY,
            backgroundColor: alpha(PRIMARY, 0.02),
            '&:hover': { backgroundColor: alpha(PRIMARY, 0.06), borderColor: alpha(PRIMARY, 0.55), color: PRIMARY_DK },
          },
          text: { color: c.textSecondary, '&:hover': { backgroundColor: c.bgSurface2, color: c.textPrimary } },
          textPrimary: { color: PRIMARY, '&:hover': { backgroundColor: alpha(PRIMARY, 0.06) } },
          sizeSmall: { padding: '5px 12px', fontSize: '0.8125rem', borderRadius: RADIUS.sm },
          sizeLarge: { padding: '11px 26px', fontSize: '0.9375rem', borderRadius: RADIUS.md },
        },
      },

      MuiButtonGroup: {
        styleOverrides: {
          root: { boxShadow: SHADOW_XS, borderRadius: RADIUS.md },
          grouped: { borderColor: c.border },
        },
      },

      MuiToggleButtonGroup: {
        styleOverrides: {
          root: { backgroundColor: c.bgInput, borderRadius: RADIUS.md, padding: 3, gap: 2 },
          grouped: { border: 'none !important', borderRadius: `${RADIUS.sm}px !important`, mx: 0 },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: RADIUS.sm,
            padding: '6px 14px',
            fontWeight: 600,
            fontSize: '0.8125rem',
            color: c.textSecondary,
            textTransform: 'none',
            transition: 'all 0.18s ease',
            '&.Mui-selected': {
              backgroundColor: c.bgSurface,
              color: c.textPrimary,
              boxShadow: SHADOW_SM,
              '&:hover': { backgroundColor: c.bgSurface },
            },
            '&:hover': { backgroundColor: alpha('#fff', 0.6) },
          },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: RADIUS.lg,
            border: `1px solid ${c.border}`,
            boxShadow: SHADOW_SM,
            background: SURFACE_GRADIENT,
            backgroundColor: c.bgSurface,
            transition: 'box-shadow 0.22s ease, border-color 0.22s ease, transform 0.22s ease',
            '&:hover': { boxShadow: SHADOW_MD, borderColor: c.borderStrong },
          },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 22, '&:last-child': { paddingBottom: 22 } } },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: { padding: '20px 22px 8px' },
          title: { fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '-0.012em', color: c.textPrimary },
          subheader: { fontSize: '0.8125rem', color: c.textMuted, marginTop: 2 },
        },
      },

      MuiTextField: {
        defaultProps: { variant: 'outlined', size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS.sm, // T289
              fontSize: '0.8125rem',
              fontFamily: '"Inter", sans-serif',
              backgroundColor: c.bgInput,
              transition: 'background 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
              minHeight: 40,
              boxShadow: 'inset 0 1px 1px rgba(15, 23, 42, 0.03)',
              '& fieldset': { borderColor: c.border, borderWidth: 1 },
              '&:hover': { backgroundColor: '#fff' },
              '&:hover fieldset': { borderColor: c.borderStrong },
              '&.Mui-focused': {
                backgroundColor: '#fff',
                boxShadow: RING_PRIMARY,
              },
              '&.Mui-focused fieldset': { borderWidth: 1.5, borderColor: PRIMARY },
              '&.Mui-error': { boxShadow: `0 0 0 3px ${alpha('#ef4444', 0.16)}` },
              '&.Mui-disabled': { backgroundColor: c.bgSurface2, opacity: 0.7 },
            },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontFamily: '"Inter", sans-serif', color: c.textMuted },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
            '& .MuiInputBase-input': { color: c.textPrimary, padding: '9px 12px' },
            '& .MuiInputBase-input::placeholder': { color: c.textMuted, opacity: 1 },
            '& .MuiFormHelperText-root': { marginLeft: 2, marginTop: 4, fontSize: '0.75rem' },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            /* T289 — his words: "in the entire app for all the tables and the intake box make the corner
               radius smaller". `!important` so a page's own sx radius (var(--radius-sm) = 8px,
               UI.radiusSm = 10px) cannot re-round an intake — one rule, app-wide, like T27 did
               for table containers. */
            borderRadius: `${RADIUS.sm}px !important`,
            backgroundColor: c.bgInput,
            boxShadow: 'inset 0 1px 1px rgba(15, 23, 42, 0.03)',
            transition: 'background 0.18s ease, box-shadow 0.18s ease',
            '&:hover': { backgroundColor: '#fff' },
            '&.Mui-focused': { backgroundColor: '#fff', boxShadow: RING_PRIMARY },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 1.5, borderColor: PRIMARY },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.borderStrong },
          },
        },
      },
      MuiSelect: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { borderRadius: `${RADIUS.sm}px !important`, fontSize: '0.8125rem', fontFamily: '"Inter", sans-serif', minHeight: 40 },
          icon: { color: c.textMuted },
        },
      },
      MuiInputLabel: {
        styleOverrides: { root: { fontSize: '0.8125rem', fontFamily: '"Inter", sans-serif', fontWeight: 400 } },
      },
      MuiFormControlLabel: {
        styleOverrides: { label: { fontSize: '0.8125rem', fontFamily: '"Inter", sans-serif' } },
      },

      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: RADIUS.lg,
            backgroundColor: c.bgSurface,
            backgroundImage: 'none',
            transition: 'box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease',
          },
          outlined: { border: `1px solid ${c.border}` },
          elevation1: { boxShadow: SHADOW_SM },
          elevation2: { boxShadow: SHADOW_MD },
          elevation3: { boxShadow: SHADOW_LG },
          elevation4: { boxShadow: SHADOW_XL },
          elevation8: { boxShadow: SHADOW_XL },
          elevation12: { boxShadow: SHADOW_2XL },
          elevation24: { boxShadow: SHADOW_2XL },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.pill,
            fontWeight: 600,
            fontSize: '0.75rem',
            letterSpacing: '0.005em',
            height: 26,
            transition: 'all 0.15s ease',
          },
          filled: {
            backgroundColor: c.bgInput,
            color: c.textSecondary,
            border: `1px solid ${c.borderSubtle}`,
            '&.MuiChip-colorPrimary': {
              background: GRAD_PRIMARY,
              color: '#fff',
              border: 'none',
              boxShadow: `0 1px 2px rgba(15,23,42,0.08), 0 4px 10px -3px ${alpha(PRIMARY, 0.45)}`,
            },
            '&.MuiChip-colorSuccess': { backgroundColor: alpha('#16a34a', 0.10), color: '#0e7a36', border: `1px solid ${alpha('#16a34a', 0.22)}` },
            '&.MuiChip-colorWarning': { backgroundColor: alpha('#f59e0b', 0.10), color: '#92400e', border: `1px solid ${alpha('#f59e0b', 0.22)}` },
            '&.MuiChip-colorError':   { backgroundColor: alpha('#ef4444', 0.10), color: '#b91c1c', border: `1px solid ${alpha('#ef4444', 0.22)}` },
            '&.MuiChip-colorInfo':    { backgroundColor: alpha('#3b82f6', 0.10), color: '#0D3D2F', border: `1px solid ${alpha('#3b82f6', 0.22)}` },
          },
          outlined: { borderWidth: 1, borderColor: c.border, backgroundColor: c.bgSurface, color: c.textSecondary },
          sizeSmall: { fontSize: '0.6875rem', height: 22 },
          label: { paddingLeft: 10, paddingRight: 10 },
        },
      },

      // T34 — one line colour for EVERY table line, app-wide. Declared on the
      // <Table> root so the descendant selector out-specifies any leftover
      // per-file `borderBottom: '1px solid #f0f0f0'` sx on individual cells:
      // those files keep their spacing/typography tweaks but can no longer
      // introduce a second, lighter colour for the horizontal row lines.
      MuiTable: {
        styleOverrides: {
          root: {
            // T216: MUI's <Table> defaults to border-collapse: collapse, which is what
            // opened the 2-device-pixel white band down the left of every MUI header
            // (Procurement). Same one definition the hand-rolled tables use.
            ...TABLE_SEAMS,
            '& .MuiTableCell-root, & .MuiTableCell-head, & th, & td': {
              borderColor: `${TABLE_BORDER} !important`,
            },
          },
        },
      },

      MuiTableHead: {
        styleOverrides: {
          root: {
            // App-wide table-header standard: ONE solid dark-green fill, white
            // text, centered, no gradients. `!important` so per-table local sx
            // (old gradients / light fills / muted text colors) can't override
            // the shared standard.
            '& .MuiTableCell-head': {
              fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase',
              letterSpacing: '0.085em',
              backgroundColor: `${TABLE_HEADER_BG} !important`,
              backgroundImage: 'none !important',
              color: '#ffffff !important',
              textAlign: 'center',
              // T34: the header/body divider and the header's vertical dividers
              // use the SAME standard line colour as every other table line —
              // they used to be dark-green / translucent-white one-offs.
              borderBottom: `${TABLE_BORDER_LINE} !important`,
              /* T329 — the header carries the grid too, so a column's line runs the full height
                 of the table. The trailing one is removed by the `:last-child` rule below, which
                 is what keeps the container's own edge clean. */
              borderRight: `${TABLE_BORDER_LINE} !important`,
              padding: '12px 18px',
              fontFamily: '"Inter", sans-serif', whiteSpace: 'nowrap',
              position: 'sticky', top: 0, zIndex: 2,
            },
            /* Kept as its own rule even though every header cell now has `none`: it is the
               last-child rule other files' `sx` used to fight, and removing it would let a
               per-page override put the trailing rule back on one table only. */
            '& .MuiTableCell-head:last-child': { borderRight: 'none !important' },
            /* T214 — FILL THE HEADER **ROW**, not just its cells, and this is the
               white-corner fix. His words: *"at the tables top left in the s.no there
               is a white space at the edge, the cell should be fully filled"*. The
               cells were already dark; the `<TableRow>` behind them was pale (pages
               set `bgcolor: '#FAFBFD'`), and the container's 5px radius with
               `overflow: hidden` CLIPS the square header — so the corner ARC showed
               the row's background through it. Colouring the cells harder could never
               reach that pixel, because the pixel belongs to the row. */
            '& .MuiTableRow-root': { backgroundColor: `${TABLE_HEADER_BG} !important` },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            /* T329 — ALL BORDERS, his rule: *"when you create a table it should have all
               borders now it dosent have the vertical column seperation lines"*. This reverses
               T322's structure half (the reference app had none) and leaves T322's radius scale
               and T9's black colour exactly as they are. */
            borderBottom: TABLE_BORDER_LINE,
            borderRight: TABLE_BORDER_LINE,
            borderColor: TABLE_BORDER,
            padding: '14px 18px',
            fontSize: '0.8125rem', color: c.textSecondary,
            fontFamily: '"Inter", sans-serif', verticalAlign: 'middle',
            /* The LAST column keeps no rule of its own — the container's border is that edge,
               and drawing both is the doubled line T216 was about. */
            '&:last-child': { borderRight: 'none' },
            // Editable cells stay click-to-edit but WITHOUT the standard-input
            // underline — the cell's own grid border is the only line.
            '& .MuiInput-root::before, & .MuiInput-root::after': { borderBottom: 'none !important' },
            '& .MuiInput-underline::before, & .MuiInput-underline::after': { borderBottom: 'none !important' },
          },
          /* T214 — "center-center", "left-center", "right-center": the SECOND word
             in each of his three alignment rules is VERTICAL, and it is the half
             that gets forgotten. Horizontal alignment is per column (see
             `utils/tableStandard.ts`); vertical is identical everywhere, so it
             lives here and reaches every MUI table in the app at once. */
          body: { color: c.textSecondary, verticalAlign: 'middle', '& strong, & b': { color: c.textPrimary, fontWeight: 600 } },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background 0.15s ease, box-shadow 0.15s ease',
            '&:hover': { backgroundColor: alpha(PRIMARY, 0.025) },
            '&.Mui-selected': { backgroundColor: alpha(PRIMARY, 0.06), '&:hover': { backgroundColor: alpha(PRIMARY, 0.09) } },
            '&:last-child td': { borderBottom: 'none' },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            // T27: ONE small radius for every table container in the app.
            // `!important` makes it a real standard — a stray local
            // `borderRadius: 2` on a TableContainer can no longer re-round it.
            borderRadius: `${TABLE_RADIUS} !important`,
            border: TABLE_BORDER_LINE,
            boxShadow: SHADOW_XS,
            background: SURFACE_GRADIENT,
            // `overflow-x: auto` also clips the corners, so the sticky dark
            // header follows the rounded top corners.
            overflowX: 'auto',
          },
        },
      },

      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: { height: 2.5, borderRadius: '2px 2px 0 0', background: GRAD_PRIMARY },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none', fontWeight: 500, fontSize: '0.875rem', minHeight: 44,
            color: c.textMuted,
            letterSpacing: '-0.005em',
            transition: 'color 0.15s ease',
            '&.Mui-selected': { fontWeight: 700, color: c.textPrimary },
            '&:hover': { color: c.textPrimary },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: RADIUS.xl,
            boxShadow: SHADOW_2XL,
            border: `1px solid ${c.border}`,
            background: SURFACE_GRADIENT,
            backgroundColor: c.bgSurface,
            backgroundImage: 'none',
          },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: {
            backgroundColor: alpha('#0b1220', 0.45),
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
          },
          invisible: { backgroundColor: 'transparent', backdropFilter: 'none', WebkitBackdropFilter: 'none' },
        },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: '1.125rem', fontWeight: 700, pb: 0.5, pt: 2.5, px: 3, color: c.textPrimary, letterSpacing: '-0.01em' } } },
      MuiDialogContent: { styleOverrides: { root: { px: 3, py: 2 } } },
      MuiDialogActions: { styleOverrides: { root: { px: 3, py: 2, gap: 1 } } },

      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: RADIUS.md, fontWeight: 500, fontSize: '0.8125rem', padding: '10px 14px', alignItems: 'center' },
          icon: { padding: '4px 0', marginRight: 10 },
          standardSuccess: { backgroundColor: alpha('#16a34a', 0.08), color: '#0e7a36', border: `1px solid ${alpha('#16a34a', 0.18)}` },
          standardError:   { backgroundColor: alpha('#ef4444', 0.08), color: '#b91c1c', border: `1px solid ${alpha('#ef4444', 0.18)}` },
          standardWarning: { backgroundColor: alpha('#f59e0b', 0.08), color: '#92400e', border: `1px solid ${alpha('#f59e0b', 0.20)}` },
          standardInfo:    { backgroundColor: alpha('#3b82f6', 0.08), color: '#0D3D2F', border: `1px solid ${alpha('#3b82f6', 0.18)}` },
          filled: { borderRadius: RADIUS.md },
          outlined: { borderRadius: RADIUS.md },
        },
      },

      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 999, height: 6, backgroundColor: c.bgInput },
          bar:  { borderRadius: 999, background: GRAD_PRIMARY },
        },
      },

      MuiCircularProgress: {
        styleOverrides: {
          colorPrimary: { color: PRIMARY },
        },
      },

      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(PRIMARY, 0.10),
            color: PRIMARY_DK,
            fontWeight: 700,
            fontSize: '0.85rem',
            border: `1px solid ${alpha(PRIMARY, 0.14)}`,
          },
        },
      },

      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md, margin: '2px 0',
            transition: 'background 0.15s ease, color 0.15s ease',
            '&.Mui-selected': {
              backgroundColor: alpha(PRIMARY, 0.08), color: PRIMARY_DK,
              boxShadow: `inset 3px 0 0 ${PRIMARY}`,
              '&:hover': { backgroundColor: alpha(PRIMARY, 0.12) },
              '& .MuiListItemIcon-root': { color: PRIMARY },
            },
            '&:hover': { backgroundColor: c.bgSurface2 },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: { color: c.textMuted, minWidth: 36 },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: '#0b1220',
            color: '#fff', borderRadius: RADIUS.sm, fontSize: '0.75rem', padding: '7px 12px',
            fontWeight: 500, boxShadow: SHADOW_LG, border: `1px solid ${alpha('#fff', 0.06)}`,
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          },
          arrow: { color: '#0b1220' },
        },
      },

      MuiFab: {
        styleOverrides: {
          primary: {
            background: GRAD_PRIMARY,
            boxShadow: `0 1px 0 0 ${alpha('#fff', 0.18)} inset, ${SHADOW_LG}, 0 12px 24px -8px ${alpha(PRIMARY, 0.45)}`,
            '&:hover': { background: GRAD_PRIMARY_HOVER, transform: 'translateY(-2px)', boxShadow: `0 1px 0 0 ${alpha('#fff', 0.22)} inset, ${SHADOW_XL}, 0 18px 32px -8px ${alpha(PRIMARY, 0.55)}` },
          },
        },
      },

      MuiDrawer: { styleOverrides: { paper: { border: 'none', backgroundColor: c.bgSurface, backgroundImage: 'none' } } },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.sm,
            background: `linear-gradient(90deg, ${c.bgSurface2} 0%, ${c.bgInput} 50%, ${c.bgSurface2} 100%)`,
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.6s ease-in-out infinite',
          },
          rounded: { borderRadius: RADIUS.md },
          circular: { borderRadius: '50%' },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: { padding: 8, width: 50, height: 30 },
          switchBase: {
            padding: 7,
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: '#fff',
              '& + .MuiSwitch-track': { background: GRAD_PRIMARY, opacity: 1, border: 'none' },
            },
          },
          track: {
            borderRadius: RADIUS.pill,
            backgroundColor: c.borderStrong,
            opacity: 1,
            transition: 'background 0.2s ease',
          },
          thumb: {
            boxShadow: '0 1px 2px rgba(15,23,42,0.20), 0 1px 3px rgba(15,23,42,0.14)',
            width: 16,
            height: 16,
          },
        },
      },

      MuiBreadcrumbs: { styleOverrides: { root: { fontSize: '0.8125rem' }, separator: { color: c.textMuted } } },

      MuiMenu: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          paper: {
            borderRadius: RADIUS.lg,
            boxShadow: SHADOW_XL,
            border: `1px solid ${c.border}`,
            backdropFilter: 'saturate(180%) blur(16px)',
            WebkitBackdropFilter: 'saturate(180%) blur(16px)',
            backgroundColor: alpha('#ffffff', 0.96),
            padding: 4,
            marginTop: 4,
          },
          list: { padding: 0 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
            borderRadius: RADIUS.sm,
            margin: '1px 2px',
            padding: '8px 12px',
            minHeight: 36,
            transition: 'background 0.12s ease, color 0.12s ease',
            color: c.textSecondary,
            '&:hover': { backgroundColor: c.bgSurface3, color: c.textPrimary },
            '&.Mui-selected': { backgroundColor: alpha(PRIMARY, 0.08), color: PRIMARY_DK, fontWeight: 600, '&:hover': { backgroundColor: alpha(PRIMARY, 0.12) } },
          },
        },
      },

      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            transition: 'background 0.15s ease, color 0.15s ease, transform 0.15s ease',
            '&:hover': { backgroundColor: c.bgSurface2 },
            '&:active': { transform: 'scale(0.96)' },
          },
        },
      },

      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 700, fontSize: '0.65rem', boxShadow: `0 0 0 2px ${c.bgSurface}`, minWidth: 18, height: 18, padding: '0 5px' },
          dot: { boxShadow: `0 0 0 2px ${c.bgSurface}` },
        },
      },

      MuiDivider: {
        styleOverrides: {
          root: { borderColor: c.borderHairline },
        },
      },

      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: alpha('#ffffff', 0.96),
            backdropFilter: 'saturate(180%) blur(16px)',
            WebkitBackdropFilter: 'saturate(180%) blur(16px)',
            border: `1px solid ${c.border}`,
            boxShadow: SHADOW_XL,
            borderRadius: RADIUS.lg,
          },
        },
      },

      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: alpha('#ffffff', 0.96),
            backdropFilter: 'saturate(180%) blur(16px)',
            WebkitBackdropFilter: 'saturate(180%) blur(16px)',
            border: `1px solid ${c.border}`,
            boxShadow: SHADOW_XL,
            borderRadius: RADIUS.xl,
          },
          option: {
            fontSize: '0.8125rem',
            borderRadius: RADIUS.sm,
            margin: '1px 4px',
            '&:hover': { backgroundColor: c.bgSurface3 },
            '&[aria-selected="true"]': { backgroundColor: alpha(PRIMARY, 0.10), color: PRIMARY_DK },
          },
          inputRoot: { paddingTop: 2, paddingBottom: 2 },
        },
      },

      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.xl,
            border: `1px solid ${c.border}`,
            backgroundImage: 'none',
            boxShadow: 'none',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { boxShadow: SHADOW_SM, borderColor: c.borderStrong },
          },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: alpha('#ffffff', 0.78),
            backgroundImage: 'none',
            color: c.textPrimary,
            backdropFilter: 'saturate(180%) blur(16px)',
            WebkitBackdropFilter: 'saturate(180%) blur(16px)',
            borderBottom: `1px solid ${c.borderSubtle}`,
            boxShadow: 'none',
            // Full-width top bar must sit flush with the sticky page header
            // beneath it; MuiPaper's default 16px radius would round the bottom
            // corners and create a visible notch at the junction.
            borderRadius: 0,
          },
        },
      },

      MuiToolbar: {
        styleOverrides: {
          root: { minHeight: 60 },
        },
      },
    },
  });
};

// Default light theme export for backwards compatibility
const theme = createAppTheme('light');
export default theme;